import { fetchDetailTransfer } from "@/api/merchant/request.get";
import { numberSeparator, toPersianNumber } from "@/hooks/useThousandsSeparator";
import CustomSelectNormal from "@/shared/components/common/select/selectNormal";
import CustomTable from "@/shared/components/common/table";
import CardKit from "@/shared/components/kit/card";
import StackKit from "@/shared/components/kit/stack";
import TypoGraphyKit from "@/shared/components/kit/typoGraphy";
import { CircularProgress } from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import moment from "jalali-moment";
import { useState, useEffect } from "react";
import { useParams } from "react-router";
/**
 * TransactionsRecord
 *
 * نمایش و مدیریت تراکنش‌های ثبت شده توسط کاربر در فروشگاه.
 * شامل فیلتر بر اساس نوع تراکنش و تعداد نمایش در صفحه.
 * داده‌ها با استفاده از React Query از API فراخوانی می‌شوند.
 *
 * @returns JSX.Element
 */

const TransactionsRecord = () => {
  const refTypeMap: Record<string, string> = {
    transfer: "انتقال",
    order: "سفارش",
    "pro-ref-earn": "کارمزد چند سطحی حرفه ای",
    "ref-earn": "کارمزد تک سطحی",
    withdraw: "برداشت",
    installments: "اقساط",
    deposite: "افزایش موجودی",
    creditScoreFee: "کارمزد اعتبار سنجی",
    resellerOrder: "-",
    bistCard: "بیست کارت",
    edit: "ویرایش",
    "pos-pazirande": "دستگاه پوز",
    "pos-ghishe": "-",
    wallet: "کیف پول",
    ipg: "Ipg",
    "edit-pazirande": "اصلاح توسط پذیرنده",
    gift_fee: "-",
    emv: "خرید emv",
    qr_code: "QR Code",
  };

  const { id } = useParams();
  const [selectedRequest, setSelectedRequest] = useState("all");
  const [selectedNumber, setSelectedNumber] = useState("5");
  const [page, setPage] = useState(1);

  const { data, isLoading,isPending } = useQuery({
    queryKey: ["transactions-data", id, selectedRequest, page, selectedNumber],
    queryFn: () => fetchDetailTransfer(id, selectedRequest, page, selectedNumber),
    refetchOnWindowFocus: false,
  });

  const dataHeader = [
    { id: "rowId", title: "" },
    { id: "amount", title: "مبلغ (ریال)" },
    { id: "type", title: "توضیحات تراکنش" },
    { id: "merchant_name", title: "نام پذیرنده" },
    { id: "ts", title: "تاریخ و ساعت تراکنش" },
  ];

  const dataNew = data?.transfers?.map((item: any, index: number) => ({
    rowId: toPersianNumber(index + 1),
    amount: numberSeparator(item.amount || 0),
    type: refTypeMap[item.type] || item.type || "-",
    merchant_name: item.merchant_name ? toPersianNumber(item.merchant_name) : "-",
    ts: `${toPersianNumber(
      moment.unix(item.ts).locale("fa").format("HH:mm")
    )} ${toPersianNumber(moment.unix(item.ts).locale("fa").format("YYYY/MM/DD"))}`,
  })) ?? [];

  const transactionTypes = [
    { label: "همه تراکنش ها", value: "all" },
    { label: "تراکنش های نقدی 20 کارت", value: "credit_20card" },
    { label: "تراکنش های اعتباری 20 کارت", value: "credit_20card" },
    { label: "تراکنش های اعتباری داخل کسب و کار", value: "credit_internal" },
  ];

  const itemsPerPageOptions = [
    { label: "5", value: "5" },
    { label: "10", value: "10" },
    { label: "15", value: "15" },
    { label: "20", value: "20" },
  ];

  useEffect(() => {
    setPage(1);
  }, [selectedRequest, selectedNumber]);

  return (
    <CardKit sx={{ bgcolor: "#2C2C2C", p: 2 }}>
      <StackKit sx={{ width: "100%" }} gap={3}>
        <TypoGraphyKit variant="body1">تراکنش ثبت شده توسط کاربر در فروشگاه</TypoGraphyKit>

        {/* Filters */}
        <StackKit direction={{ xs: "column", sm: "row" }} gap={3} justifyContent="space-between" alignItems={{ xs: "flex-start", sm: "center" }} width="100%">
          <StackKit direction={{ xs: "column", sm: "row" }} gap={2} width={{ xs: "100%", sm: "48%" }}>
            <TypoGraphyKit variant="body1">نوع تراکنش</TypoGraphyKit>
            <CustomSelectNormal label="" value={selectedRequest} onChange={(e, item) => setSelectedRequest(item?.props?.value || "all")} options={transactionTypes} getOptionLabel={(item) => toPersianNumber(`${item.label}`)} getOptionValue={(item) => item.value} width="100%" />
          </StackKit>

          <StackKit direction={{ xs: "column", sm: "row" }} gap={2} width={{ xs: "100%", sm: "48%" }} alignItems={{ xs: "flex-start", sm: "center" }}>
            <TypoGraphyKit variant="body1">تعداد نمایش در صفحه</TypoGraphyKit>
            <CustomSelectNormal label="" value={selectedNumber} onChange={(e, item) => setSelectedNumber(item?.props?.value || "5")} options={itemsPerPageOptions} getOptionLabel={(item) => `${item.label}`} getOptionValue={(item) => item.value} width="100%" />
          </StackKit>
        </StackKit>

        {/* Transactions Table */}
        <div className={`overflow-x-auto relative ${isLoading || isPending ? 'h-[250px]' : '' }`}>
          {isLoading || isPending ?
            <div style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0, 0, 0, 0.5)', display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 1000, borderRadius: '8px' }}>
              <CircularProgress sx={{ color: '#fff' }} />
            </div>
            :
            <CustomTable dataHeader={dataHeader} dataTbody={dataNew} total={data?.total || dataNew.length} currentPage={page} setPages={setPage} itemsPerPage={Number(selectedNumber)} colorPagination="#E5613E" />
          }
        </div>
      </StackKit>
    </CardKit>
  );
};

export default TransactionsRecord;
